import React, { useEffect, useState, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, Polygon } from '@react-google-maps/api';
import {
    Box,
    Button,
    Typography,
    Autocomplete,
    FormControl,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Checkbox,
    FormControlLabel,
    useMediaQuery,
    Drawer,
    Pagination,
    TextField
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import EditIcon from '@mui/icons-material/Edit';
import { getCoordinatesFromPolygonText, getPolygonCentroid } from '../../../utils';
import EditBlockDialog from './EditBlockDialog/EditBlockDialog';
import { useNavigate } from 'react-router-dom'; // <-- Add this import

const MAP_STATS_HEIGHT = '950px';
const SUBLOCATIONS_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/api/sublocations/all`;

function BlocksView() {
    const [farms, setFarms] = useState([]);
    const [selectedFarm, setSelectedFarm] = useState('');
    const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 });
    const [mapZoom, setMapZoom] = useState(12);
    const [sublocations, setSublocations] = useState([]);
    const mapRef = useRef(null);

    // Map Options state
    const [showBlockNames, setShowBlockNames] = useState(false);
    const [isMapOptionsBoxOpen, setIsMapOptionsBoxOpen] = useState(true);
    const isMobile = useMediaQuery('(max-width:600px)');
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editBlock, setEditBlock] = useState(null);

    // Sorting and paging state
    const [sortBy, setSortBy] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 10;

    // Filter state
    const [blockFilter, setBlockFilter] = useState('');

    const navigate = useNavigate(); // <-- Add this hook

    // Load sublocations on page load
    useEffect(() => {
        async function fetchSublocations() {
            try {
                const res = await fetch(SUBLOCATIONS_ENDPOINT);
                const data = await res.json();
                setSublocations(Array.isArray(data) ? data : []);

                // Derive unique farmNames, sort alphabetically
                const farmNames = Array.from(
                    new Set(
                        (Array.isArray(data) ? data : [])
                            .map(b => b.farmName)
                            .filter(name => !!name && typeof name === 'string')
                    )
                );
                setFarms(
                    farmNames
                        .map(name => ({ id: name, name }))
                        .sort((a, b) => a.name.localeCompare(b.name))
                );
                // Set default selected farm
                if (farmNames.length > 0) {
                    setSelectedFarm(farmNames[0]);
                }
            } catch {
                setSublocations([]);
                setFarms([]);
            }
        }
        fetchSublocations();
    }, []);

    // Update map center, zoom, and pin when farm changes
    useEffect(() => {
        // Find the first block for the selected farm with valid boundaries
        const firstBlock = sublocations.find(
            b => b.farmName === selectedFarm && b.boundaries
        );
        if (firstBlock) {
            const polygons = getCoordinatesFromPolygonText(firstBlock.boundaries);
            if (Array.isArray(polygons) && polygons.length > 0) {
                const centroid = getPolygonCentroid(polygons);
                if (centroid) {
                    setMapCenter(centroid);
                    setMapZoom(18);
                    if (mapRef.current) {
                        mapRef.current.panTo(centroid);
                        mapRef.current.setZoom(16);
                    }
                }
            }
        }
    }, [selectedFarm, sublocations]);

    const selectedFarmObj = useMemo(
        () => farms.find(f => f.id === selectedFarm) || null,
        [farms, selectedFarm]
    );

    // Filter sublocations by selected farm and block name filter
    const filteredBlocks = useMemo(
        () => sublocations.filter(
            b =>
                b.farmName === selectedFarm &&
                (!blockFilter ||
                    (b.name && b.name.toLowerCase().includes(blockFilter.toLowerCase())))
        ),
        [sublocations, selectedFarm, blockFilter]
    );

    // Sorting logic for blocks
    const sortedBlocks = useMemo(() => {
        const blocks = [...filteredBlocks];
        blocks.sort((a, b) => {
            let aValue, bValue;
            switch (sortBy) {
                case 'farm':
                    aValue = selectedFarmObj?.name || '';
                    bValue = selectedFarmObj?.name || '';
                    break;
                case 'name':
                    aValue = a.name || '';
                    bValue = b.name || '';
                    break;
                case 'areaAcres':
                    aValue = Number(a.areaAcres) || 0;
                    bValue = Number(b.areaAcres) || 0;
                    break;
                case 'crop':
                    aValue = a.crop || '';
                    bValue = b.crop || '';
                    break;
                case 'rowWidthFt':
                    aValue = Number(a.rowWidthFt) || 0;
                    bValue = Number(b.rowWidthFt) || 0;
                    break;
                default:
                    aValue = '';
                    bValue = '';
            }
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            }
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        });
        return blocks;
    }, [filteredBlocks, sortBy, sortDirection, selectedFarmObj]);

    // Paging logic
    const pageCount = Math.max(1, Math.ceil(sortedBlocks.length / PAGE_SIZE));
    const pagedBlocks = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return sortedBlocks.slice(start, start + PAGE_SIZE);
    }, [sortedBlocks, page]);

    // Reset to page 1 when farm or sort changes
    useEffect(() => {
        setPage(1);
    }, [selectedFarm, sortBy, sortDirection]);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            setSortDirection('asc');
        }
    };

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
    });

    const handleMapLoad = (map) => {
        mapRef.current = map;
        if (selectedFarmObj && selectedFarmObj.lat && selectedFarmObj.long) {
            map.panTo({ lat: selectedFarmObj.lat, lng: selectedFarmObj.long });
            map.setZoom(16);
        }
    };

    // Center map on block boundaries centroid when row is clicked
    const handleRowClick = (block) => {
        const paths = getCoordinatesFromPolygonText(block.boundaries) || [];
        if (paths.length > 0) {
            const centroid = getPolygonCentroid(paths);
            if (centroid) {
                setMapCenter(centroid);
                setMapZoom(18);
                if (mapRef.current) {
                    mapRef.current.panTo(centroid);
                    mapRef.current.setZoom(18);
                }
            }
        }
    };

    // Edit dialog handlers
    const handleEditClick = (block) => {
        setEditBlock(block);
        setEditDialogOpen(true);
    };

    const handleEditDialogClose = () => {
        setEditDialogOpen(false);
        setEditBlock(null);
    };

    const handleEditDialogSuccess = () => {
        setEditDialogOpen(false);
        setEditBlock(null);
        // Refresh sublocations after edit
        (async function fetchSublocations() {
            try {
                const res = await fetch(SUBLOCATIONS_ENDPOINT);
                const data = await res.json();
                setSublocations(Array.isArray(data) ? data : []);
            } catch {
                setSublocations([]);
            }
        })();
    };

    // Map Options UI
    const controls = (
        <Box
            sx={{
                minWidth: isMapOptionsBoxOpen ? 180 : 160,
                width: isMapOptionsBoxOpen ? 275 : 160,
                bgcolor: '#fff',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                zIndex: 10,
                overflow: 'hidden',
                transition: 'width 0.2s, min-width 0.2s',
            }}
        >
            <Box
                onClick={() => setIsMapOptionsBoxOpen(open => !open)}
                sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: isMapOptionsBoxOpen ? '6px 12px' : '6px 12px',
                    bgcolor: '#f5f5f5',
                    fontWeight: 400,
                    fontSize: 16,
                    borderBottom: isMapOptionsBoxOpen ? '1px solid #eee' : 'none',
                    minHeight: 40,
                    height: 40,
                    transition: 'all 0.2s',
                }}
            >
                <Typography
                    variant="subtitle1"
                    component="span"
                    sx={{
                        fontWeight: 500,
                        fontSize: '1rem',
                        color: 'text.primary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        flex: 1,
                        transition: 'font-size 0.2s',
                    }}
                >
                    Map Options
                </Typography>
                {isMapOptionsBoxOpen ? <ChevronLeft /> : <ChevronRight />}
            </Box>
            {isMapOptionsBoxOpen && (
                <Box sx={{ p: 2.5 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showBlockNames}
                                onChange={e => setShowBlockNames(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>
                                Show Block Names
                            </Typography>
                        }
                    />
                </Box>
            )}
        </Box>
    );

    // Map style
    const mapTypeId = 'satellite';
    const mapContainerStyle = {
        width: '100%',
        height: '900px',
        borderRadius: '20px'
    };

    return (
        <div
            style={{
                alignItems: 'stretch',
                background: 'white',
                minHeight: '100vh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
                padding: '40px 40px'
            }}
        >
            {/* Top Controls */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'flex-end',
                    marginBottom: 24,
                    gap: isMobile ? 12 : 32,
                }}
            >
                {/* Farm combobox */}
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
                    <Typography variant="h6" sx={{ marginBottom: 0.5 }}>
                        <strong>Farm</strong>
                    </Typography>
                    <Autocomplete
                        size="small"
                        fullWidth
                        options={farms}
                        getOptionLabel={option => option?.name || ''}
                        value={selectedFarmObj}
                        onChange={(_, newValue) => {
                            const newFarmId = newValue ? newValue.id : '';
                            setSelectedFarm(newFarmId);
                            if (newFarmId && newValue.lat && newValue.long) {
                                setMapCenter({ lat: newValue.lat, lng: newValue.long });
                                setMapZoom(16);
                                if (mapRef.current) {
                                    mapRef.current.panTo({ lat: newValue.lat, lng: newValue.long });
                                    mapRef.current.setZoom(16);
                                }
                            }
                        }}
                        renderInput={params => (
                            <FormControl size="small" fullWidth>
                                <div ref={params.InputProps.ref}>
                                    <input
                                        {...params.inputProps}
                                        id="farm-autocomplete"
                                        style={{
                                            width: '100%',
                                            padding: '8.5px 14px',
                                            fontSize: '1rem',
                                            border: '1px solid #c4c4c4',
                                            borderRadius: 4
                                        }}
                                        placeholder="Type to search farms..."
                                    />
                                </div>
                            </FormControl>
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disabled={farms.length === 0}
                        clearOnEscape
                        clearOnBlur
                    />
                </div>
            </div>
            {/* Main content: grid and map */}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 24,
                    width: '100%',
                    minHeight: 0,
                    flex: 1,
                    paddingRight: 32,
                    boxSizing: 'border-box'
                }}
            >
                {/* Grid: 1/3 width */}
                <Box
                    sx={{
                        flex: 1,
                        maxWidth: '33.3333%',
                        height: '100%',
                        borderRight: 1,
                        borderColor: 'divider',
                        p: 2,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'white',
                        border: '2px solid #222',
                        borderRadius: 2,
                        boxSizing: 'border-box',
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Blocks
                    </Typography>
                    <TextField
                        label="Filter by Block Name"
                        variant="outlined"
                        size="small"
                        value={blockFilter}
                        onChange={e => setBlockFilter(e.target.value)}
                        sx={{ mb: 2 }}
                        fullWidth
                    />
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        onClick={() => handleSort('farm')}
                                        style={{ cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        Farm {sortBy === 'farm' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('name')}
                                        style={{ cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        Block {sortBy === 'name' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('areaAcres')}
                                        style={{ cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        Size (ac) {sortBy === 'areaAcres' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('crop')}
                                        style={{ cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        Crop {sortBy === 'crop' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                                    </TableCell>
                                    <TableCell
                                        onClick={() => handleSort('rowWidthFt')}
                                        style={{ cursor: 'pointer', fontWeight: 'bold' }}
                                    >
                                        Row Width (ft) {sortBy === 'rowWidthFt' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 'bold' }}>
                                        Edit
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pagedBlocks.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ color: '#888' }}>
                                            No blocks found for this farm.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pagedBlocks.map(block => (
                                        <TableRow
                                            key={block.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => handleRowClick(block)}
                                        >
                                            <TableCell>
                                                {selectedFarmObj ? selectedFarmObj.name : ''}
                                            </TableCell>
                                            <TableCell>{block.name}</TableCell>
                                            <TableCell>{block.areaAcres !== undefined ? Number(block.areaAcres).toFixed(1) : '-'}</TableCell>
                                            <TableCell>{block.crop || '-'}</TableCell>
                                            <TableCell>{block.rowWidthFt !== undefined ? Number(block.rowWidthFt).toFixed(1) : '-'}</TableCell>
                                            <TableCell>
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    aria-label="Edit"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleEditClick(block);
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Paging controls */}
                    {pageCount > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={pageCount}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                color="primary"
                                size="small"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => navigate('/settings/blocks-import')}
                        >
                            Import
                        </Button>
                    </Box>
                </Box>
                {/* Map: 2/3 width */}
                <Box
                    sx={{
                        flex: 2,
                        maxWidth: '66.6667%',
                        height: MAP_STATS_HEIGHT,
                        minHeight: MAP_STATS_HEIGHT,
                        paddingLeft: 2,
                        display: 'flex',
                        position: 'relative',
                        borderRadius: 3,
                        overflow: 'hidden',
                        background: 'white',
                        boxSizing: 'border-box',
                        mb: 0
                    }}
                >
                    {/* Map Options Drawer/Button */}
                    {isMobile ? (
                        <>
                            <IconButton
                                style={{
                                    position: 'absolute',
                                    top: 10,
                                    right: 10,
                                    zIndex: 10001,
                                    background: '#fff',
                                    boxShadow: 1
                                }}
                                onClick={() => setDrawerOpen(true)}
                                size="large"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Drawer
                                anchor="right"
                                open={drawerOpen}
                                onClose={() => setDrawerOpen(false)}
                            >
                                <Box sx={{ p: 2 }}>
                                    {controls}
                                </Box>
                            </Drawer>
                        </>
                    ) : (
                        <div
                            style={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                zIndex: 10000
                            }}
                        >
                            {controls}
                        </div>
                    )}
                    {isLoaded ? (
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={mapCenter}
                            zoom={mapZoom}
                            onLoad={handleMapLoad}
                            options={{
                                mapTypeId: mapTypeId,
                                streetViewControl: false,
                                fullscreenControl: false,
                                mapTypeControl: true,
                                mapTypeControlOptions: {
                                    style: window.google && window.google.maps
                                        ? window.google.maps.MapTypeControlStyle.DROPDOWN_MENU
                                        : undefined,
                                    position: window.google && window.google.maps
                                        ? window.google.maps.ControlPosition.TOP_LEFT
                                        : undefined
                                }
                            }}
                        >
                            {selectedFarmObj && selectedFarmObj.lat && selectedFarmObj.long && (
                                <Marker
                                    position={{ lat: selectedFarmObj.lat, lng: selectedFarmObj.long }}
                                    title={selectedFarmObj.name}
                                />
                            )}
                            {filteredBlocks.map(block => {
                                const paths = getCoordinatesFromPolygonText(block.boundaries);
                                return Array.isArray(paths) && paths.length > 0 ? (
                                    <React.Fragment key={block.id}>
                                        <Polygon
                                            paths={paths}
                                            options={{
                                                fillColor: '#388e3c',
                                                fillOpacity: 0.2,
                                                strokeColor: '#1976d2',
                                                strokeOpacity: 0.8,
                                                strokeWeight: 3,
                                                clickable: false,
                                                zIndex: 2
                                            }}
                                        />
                                        {showBlockNames && (
                                            <Marker
                                                position={getPolygonCentroid(paths)}
                                                label={{
                                                    text: block.name,
                                                    color: "#222",
                                                    fontWeight: "bold",
                                                    fontSize: "16px"
                                                }}
                                                icon={{
                                                    path: window.google && window.google.maps
                                                        ? window.google.maps.SymbolPath.CIRCLE
                                                        : "",
                                                    scale: 0,
                                                    fillOpacity: 0,
                                                    strokeOpacity: 0
                                                }}
                                                zIndex={1000}
                                            />
                                        )}
                                    </React.Fragment>
                                ) : null;
                            })}
                        </GoogleMap>
                    ) : (
                        <Box sx={{ p: 2 }}>Loading map...</Box>
                    )}
                </Box>
            </div>
            {/* Edit Block Dialog */}
            <EditBlockDialog
                location={editBlock}
                open={editDialogOpen}
                onClose={handleEditDialogClose}
                onSuccess={handleEditDialogSuccess}
            />
        </div>
    );
}

export default BlocksView;